import { Dropdown as LibDropdown, DropdownOptions as LibDropdownOptions } from 'qampo-ui-library';
import type { ReactNode } from 'react';

type DropdownSize = 'md' | 'sm';

const DROPDOWN_BUTTON_STYLES: Record<DropdownSize, React.CSSProperties> = {
  md: {
    background: 'var(--color-surface-primary)',
    borderRadius: 'var(--radius-regular)',
    padding: '6px 16px 6px 16px',
    border: '1px solid var(--color-border-primary)'
  },
  sm: {
    background: 'var(--color-surface-primary)',
    borderRadius: 'var(--radius-xs)',
    padding: '3px 8px',
    border: '1px solid var(--color-border-primary)',
    minHeight: 'auto',
    fontSize: 'var(--typography-size-text-label)'
  }
};

const DROPDOWN_BUTTON_TEXT_STYLES: Record<DropdownSize, React.CSSProperties> = {
  md: {},
  sm: { fontSize: 'var(--typography-size-text-label)' }
};

const DROPDOWN_LIST_STYLES: React.CSSProperties = {
  background: 'var(--color-surface-primary)',
  border: '1px solid var(--color-border-primary)'
};

interface DropdownProps {
  label?: string;
  children?: React.ReactNode;
  placeholder?: string;
  error?: boolean;
  required?: boolean;
  selectedOption?: React.ReactNode;
  disable?: boolean;
  helperText?: string;
  width?: number;
  helperTextWidth?: number;
  classes?: string;
  widthClasses?: string;
  size?: DropdownSize;
  style?: React.CSSProperties;
  styles?: {
    root?: React.CSSProperties;
    label?: React.CSSProperties;
    button?: React.CSSProperties;
    buttonText?: React.CSSProperties;
    optionsList?: React.CSSProperties;
    helperText?: React.CSSProperties;
  };
}

export const Dropdown = ({ size = 'md', styles, ...props }: DropdownProps) => {
  const mergedStyles = {
    ...styles,
    button: { ...DROPDOWN_BUTTON_STYLES[size], ...styles?.button },
    buttonText: { ...DROPDOWN_BUTTON_TEXT_STYLES[size], ...styles?.buttonText },
    optionsList: { ...DROPDOWN_LIST_STYLES, ...styles?.optionsList }
  };
  return <LibDropdown {...props} styles={mergedStyles} />;
};

const DROPDOWN_OPTION_STYLES: Record<DropdownSize, React.CSSProperties> = {
  md: {},
  sm: { padding: '4px 10px' }
};

const DROPDOWN_OPTION_TEXT_STYLES: Record<DropdownSize, React.CSSProperties> = {
  md: {},
  sm: { fontSize: 'var(--typography-size-text-label)' }
};

interface DropdownOptionsProps<T> {
  options: T[];
  selectedOption: T | null;
  getKey: (option: T) => string | number;
  displayOption: (option: T) => ReactNode;
  handleChange: (value: T) => void;
  disabledOptions?: T[];
  classes?: string;
  size?: DropdownSize;
  style?: React.CSSProperties;
  styles?: {
    option?: React.CSSProperties;
    optionText?: React.CSSProperties;
  };
}

export const DropdownOptions = <T,>({ size = 'md', styles, ...props }: DropdownOptionsProps<T>) => {
  const mergedStyles = {
    option: { ...DROPDOWN_OPTION_STYLES[size], ...styles?.option },
    optionText: { ...DROPDOWN_OPTION_TEXT_STYLES[size], ...styles?.optionText }
  };
  return <LibDropdownOptions {...props} styles={mergedStyles} />;
};
